/**
 * @fileoverview Aba Histórico de Equipe e permissões: quem mudou o acesso de
 * quem, quando e o que mudou (o perfil, a licença e as exceções, antes e
 * depois), das mudanças mais novas para as mais antigas, com filtro por pessoa
 * (PersonFilter: busca pelo nome, com foto) e período. O servidor não mostra o
 * super: quando é ele quem muda, sai como o suporte.
 */

import React, { useEffect, useRef, useState } from "react";
import { TextField } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { LoadError, LoadingState } from "../Settings/LoadState";
import { formatDateTime, licenseLabel, permissionTitle, roleLabel, scopeLabel } from "./labels";
import PersonFilter from "./PersonFilter";
import useTeamAccessStyles from "./styles";

const t = (key, options) => i18n.t(`accessControl.${key}`, options);

const PROFILE_ACTIONS = ["conversion", "user.create", "user.update"];

const personName = (person) => (person?.support ? t("history.support") : person?.name || t("history.removed"));

const authorLine = ({ actor, actingAs }) => {
  if (!actor) return t("history.system");
  if (actingAs) return t("history.byAs", { name: personName(actor), as: personName(actingAs) });
  return t("history.by", { name: personName(actor) });
};

const overrideName = ({ permission, effect, scope }) =>
  effect === "grant" && scope ? `${permissionTitle(permission)} (${scopeLabel(scope)})` : permissionTitle(permission);

// O que mudou, numa linha por item: o perfil e a licença, ou cada exceção.
const changeLines = (entry) => {
  const before = entry.before || null;
  const after = entry.after || null;
  if (PROFILE_ACTIONS.includes(entry.action)) {
    if (!after) return [];
    const dash = "—";
    return [
      !before || before.role !== after.role
        ? t("history.role", { from: before ? roleLabel(before.role) : dash, to: roleLabel(after.role) })
        : null,
      !before || before.accessScope !== after.accessScope
        ? t("history.license", {
            from: before ? licenseLabel(before.accessScope) : dash,
            to: licenseLabel(after.accessScope),
          })
        : null,
    ].filter(Boolean);
  }
  const was = new Map((before?.overrides || []).map((item) => [item.permission, item]));
  const now = new Map((after?.overrides || []).map((item) => [item.permission, item]));
  const lines = [];
  now.forEach((item, key) => {
    const old = was.get(key);
    if (old && old.effect === item.effect && (old.scope || null) === (item.scope || null)) return;
    lines.push(t(item.effect === "grant" ? "history.granted" : "history.denied", { name: overrideName(item) }));
  });
  was.forEach((item, key) => {
    if (!now.has(key)) lines.push(t("history.cleared", { name: permissionTitle(key) }));
  });
  return lines;
};

// O início e o fim do dia escolhido, no fuso de quem vê.
const dayStart = (day) => (day ? new Date(`${day}T00:00:00`).toISOString() : undefined);
const dayEnd = (day) => (day ? new Date(`${day}T23:59:59.999`).toISOString() : undefined);

const HistoryTab = () => {
  const classes = useTeamAccessStyles();
  const [person, setPerson] = useState(null);
  const userId = person ? person.id : "all";
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [state, setState] = useState({ entries: [], next: null, status: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  const fetchPage = (before) =>
    api
      .get("/access-control/audit", {
        params: {
          userId: userId === "all" ? undefined : userId,
          from: dayStart(from),
          to: dayEnd(to),
          before,
        },
      })
      .then(({ data }) => data);

  const load = () => {
    seq.current += 1;
    const current = seq.current;
    setState((prev) => ({ ...prev, status: "loading" }));
    fetchPage()
      .then((data) => {
        if (seq.current === current) setState({ entries: data.entries, next: data.next, status: "ready" });
      })
      .catch(() => {
        if (seq.current === current) setState({ entries: [], next: null, status: "error" });
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, from, to]);

  const loadMore = () => {
    const current = seq.current;
    setLoadingMore(true);
    fetchPage(state.next)
      .then((data) => {
        if (seq.current === current) {
          setState((prev) => ({ ...prev, entries: [...prev.entries, ...data.entries], next: data.next }));
        }
      })
      .catch(toastError)
      .finally(() => setLoadingMore(false));
  };

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <PersonFilter value={person} onChange={setPerson} />
        <TextField
          size="small"
          variant="outlined"
          type="date"
          label={t("history.from")}
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          InputLabelProps={{ shrink: true }}
          className={classes.dateField}
        />
        <TextField
          size="small"
          variant="outlined"
          type="date"
          label={t("history.to")}
          value={to}
          onChange={(event) => setTo(event.target.value)}
          InputLabelProps={{ shrink: true }}
          className={classes.dateField}
        />
      </div>

      {state.status === "loading" && <LoadingState />}
      {state.status === "error" && <LoadError onRetry={load} />}
      {state.status === "ready" && (
        <div className={classes.card}>
          {state.entries.length === 0 && <div className={classes.empty}>{t("history.empty")}</div>}
          {state.entries.map((entry) => {
            const lines = changeLines(entry);
            return (
              <div key={entry.id} className={classes.historyRow}>
                <div className={classes.historyTop}>
                  <span className={classes.historyWhat}>
                    {entry.target?.name || t("history.removed")} · {t(`history.actions.${entry.action}`)}
                  </span>
                  <span className={classes.historyWhen}>{formatDateTime(entry.createdAt)}</span>
                </div>
                <div className={classes.historyWho}>{authorLine(entry)}</div>
                {lines.length > 0 && (
                  <ul className={classes.historyChanges}>
                    {lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {state.next && (
            <div className={classes.loadMore}>
              <button type="button" className={classes.secondaryButton} onClick={loadMore} disabled={loadingMore}>
                {t("history.loadMore")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryTab;
